import React, { useCallback, useEffect, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Input,
    Checkbox,
    Form,
    Space,
    message,
    Typography,
    Card,
    Select,
    InputNumber,
    Divider
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    PlusCircleOutlined,
} from "@ant-design/icons";
import { Copy, PlusCircle, Trash2 } from "lucide-react";
import { questionService } from "../../services/questionService";
import { questionSkillService } from "../../services/questionSkillService";

interface ApiResponse {
    req: {
        page: string;
        limit: string;
        search: string;
    };
    data: [];
    total: number;
    currentPage: number;
    totalPages: number;
}

const QuestionManagement: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [questionSkills, setQuestionSkills] = useState([]);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const response: ApiResponse = await questionService.getQuestions({
                page: currentPage,
                pageSize: pageSize
            });

            setQuestions(response.data);
            setTotal(response.total);
        } catch (error) {
            message.error('Không thể tải danh sách câu hỏi');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    const fetchQuestionSkills = useCallback(async () => {
        try {
            setLoading(true);
            const response = await questionSkillService.getQuestionSkills();
            setQuestionSkills(response as any);
        } catch (error) {
            message.error('Không thể tải danh sách loại câu hỏi');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQuestions();
        fetchQuestionSkills();
    }, [fetchQuestions, fetchQuestionSkills]);

    const handleAdd = () => {
        setEditingQuestion({
            title: "",
            content: "",
            SubQuestions: []
        });
        setShowEditModal(true);
    };

    const handleEdit = (question) => {
        setEditingQuestion({ ...question });
        setShowEditModal(true);
    };

    const handleDelete = async (questionId) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc chắn muốn xóa câu hỏi này không?',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    await questionService.deleteQuestion(questionId);
                    setQuestions(questions.filter((q) => q.id !== questionId));
                    message.success("Đã xóa câu hỏi thành công.");
                } catch (error) {
                    message.error("Có lỗi xảy ra khi xóa câu hỏi.");
                }
            },
        });
    };

    const handleSave = async () => {
        if (editingQuestion?.SubQuestions?.length === 0) {
            message.error("Vui lòng thêm ít nhất một câu hỏi phụ.");
            return;
        }

        try {
            let response;
            if (editingQuestion?.id) {
                response = await questionService.updateQuestion(editingQuestion);
                setQuestions(questions.map((q) =>
                    q.id === editingQuestion.id ? response : q
                ));
                message.success("Câu hỏi đã được cập nhật thành công.");
            } else {
                response = await questionService.createQuestion(editingQuestion);
                setQuestions([...questions, response]);
                message.success("Câu hỏi đã được tạo thành công.");
            }

            setShowEditModal(false);
            setEditingQuestion(null);
            await fetchQuestions();
        } catch (error) {
            console.log(error);
            message.error("Có lỗi xảy ra khi lưu câu hỏi.");
        }
    };

    const addAnswer = (subQuestionIndex) => {
        const updatedSubQuestions = [...editingQuestion?.SubQuestions];
        updatedSubQuestions[subQuestionIndex].Answers.push({
            content: '',
            isCorrect: '0',
            correctPosition: 0
        });
        setEditingQuestion(prev => ({
            ...prev,
            SubQuestions: updatedSubQuestions
        }));
    };

    const columns = [
        {
            title: "Câu hỏi số",
            dataIndex: "no",
            key: "no",
            width: 120
        },
        {
            title: "Loại câu hỏi",
            dataIndex: "typeQuestion",
            key: "typeQuestion",
            width: 150,
            render: (_, record) => {
                return (
                    record?.QuestionSkill?.TypeQuestion?.name
                )
            }
        },
        {
            title: "Tiêu đề",
            dataIndex: "title",
            key: "title",
            width: 400,
            ellipsis: true,
            render: (text) => (
                <Typography.Paragraph
                    ellipsis={{ rows: 2, tooltip: text }}
                    style={{ marginBottom: 0 }}
                >
                    {text}
                </Typography.Paragraph>
            )
        },
        {
            title: "Nội dung",
            dataIndex: "content",
            key: "content",
            width: 400,
            ellipsis: true,
            render: (text) => (
                <Typography.Paragraph
                    ellipsis={{ rows: 3, tooltip: text }}
                    style={{ marginBottom: 0 }}
                >
                    {text}
                </Typography.Paragraph>
            )
        },
        {
            title: "Link âm thanh",
            dataIndex: "linkAudio",
            key: "linkAudio",
            width: 250,
            ellipsis: true,
            render: (text) => (
                <Typography.Paragraph
                    ellipsis={{ rows: 3, tooltip: text }}
                    style={{ marginBottom: 0 }}
                >
                    {text}
                </Typography.Paragraph>
            )
        },
        {
            title: "Thao tác",
            key: "actions",
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="default"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                    />
                </Space>
            ),
        },
    ];

    const renderSubQuestions = () => {
        return (
            <div className="space-y-4">
                {
                    editingQuestion?.SubQuestions?.map((subQ, subIndex) => {
                        return (
                            <Card key={subQ?.id}>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-base font-medium">Câu hỏi phụ {subIndex + 1}</h3>
                                        <Space>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    const newSubQuestion = JSON.parse(JSON.stringify(subQ));
                                                    delete newSubQuestion.id;
                                                    newSubQuestion.Answers = newSubQuestion.Answers.map(answer => {
                                                        const newAnswer = { ...answer };
                                                        delete newAnswer.id;
                                                        return newAnswer;
                                                    });

                                                    setEditingQuestion(prev => ({
                                                        ...prev,
                                                        SubQuestions: [
                                                            ...prev.SubQuestions.slice(0, subIndex + 1),
                                                            newSubQuestion,
                                                            ...prev.SubQuestions.slice(subIndex + 1)
                                                        ]
                                                    }));
                                                }}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    const newSubQuestions = editingQuestion?.SubQuestions?.filter((_, i) => i !== subIndex);
                                                    setEditingQuestion(prev => ({
                                                        ...prev,
                                                        SubQuestions: newSubQuestions
                                                    }));
                                                }}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </Space>
                                    </div>

                                    {/* Form nhập tiêu đề và điểm */}
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                        <Form.Item label="Tiêu đề" className="col-span-4">
                                            <Input.TextArea
                                                rows={5}
                                                placeholder="Tiêu đề"
                                                value={subQ?.title}
                                                onChange={(e) => {
                                                    const updated = [...editingQuestion?.SubQuestions];
                                                    updated[subIndex].title = e.target.value;
                                                    setEditingQuestion(prev => ({
                                                        ...prev,
                                                        SubQuestions: updated
                                                    }));
                                                }}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Điểm" className="col-span-1">
                                            <Input
                                                type="number"
                                                placeholder="Điểm"
                                                value={subQ?.point}
                                                onChange={(e) => {
                                                    const updated = [...editingQuestion?.SubQuestions];
                                                    updated[subIndex].point = Number(e.target.value);
                                                    setEditingQuestion(prev => ({
                                                        ...prev,
                                                        SubQuestions: updated
                                                    }));
                                                }}
                                            />
                                        </Form.Item>
                                    </div>

                                    {/* Quản lý đáp án */}
                                    <div className="space-y-2">
                                        <h4 className="font-medium">Danh sách đáp án:</h4>
                                        {subQ.Answers.map((answer, answerIndex) => (
                                            <div key={answer.id} className="flex items-center space-x-2">
                                                <Input
                                                    placeholder="Nội dung đáp án"
                                                    value={answer.content}
                                                    onChange={(e) => {
                                                        const updated = [...editingQuestion?.SubQuestions];
                                                        updated[subIndex].Answers[answerIndex].content = e.target.value;
                                                        setEditingQuestion(prev => ({
                                                            ...prev,
                                                            SubQuestions: updated
                                                        }));
                                                    }}
                                                />
                                                <Checkbox
                                                    checked={answer.isCorrect === '1'}
                                                    onChange={(e) => {
                                                        const updated = [...editingQuestion?.SubQuestions];
                                                        updated[subIndex].Answers[answerIndex].isCorrect = e.target.checked ? '1' : '0';
                                                        setEditingQuestion(prev => ({
                                                            ...prev,
                                                            SubQuestions: updated
                                                        }));
                                                    }}
                                                />
                                                <InputNumber
                                                    value={answer?.correctPosition}
                                                    onChange={(value) => {
                                                        const updated = [...editingQuestion?.SubQuestions];
                                                        updated[subIndex].Answers[answerIndex].correctPosition = value;
                                                        setEditingQuestion(prev => ({
                                                            ...prev,
                                                            SubQuestions: updated
                                                        }));
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        const updated = [...editingQuestion?.SubQuestions];
                                                        updated[subIndex].Answers = updated[subIndex].Answers.filter((_, i) => i !== answerIndex);
                                                        setEditingQuestion(prev => ({
                                                            ...prev,
                                                            SubQuestions: updated
                                                        }));
                                                    }}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}

                                        {/* Thêm đáp án mới */}
                                        <Button
                                            onClick={() => addAnswer(subIndex)}
                                            size="middle"
                                            className="w-full"
                                        >
                                            <PlusCircle className="h-4 w-4 mr-2" />
                                            Thêm đáp án
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        )
                    })
                }

                <Button
                    type="dashed"
                    onClick={() => {
                        setEditingQuestion((prev) => ({
                            ...prev,
                            SubQuestions: [
                                ...prev?.SubQuestions,
                                {
                                    title: "",
                                    point: 1,
                                    Answers: []
                                }
                            ]
                        }));
                    }}
                >
                    <PlusCircleOutlined /> Thêm câu hỏi phụ
                </Button>
            </div>
        );
    }

    const handleTableChange = (newPagination: any) => {
        setCurrentPage(newPagination.current);
        setPageSize(newPagination.pageSize);
    };

    return (
        <div className="p-6">
            <div className="flex justify-between mb-4">
                <h1 className="text-2xl font-bold">Quản Lý Câu hỏi</h1>
                <Button type="primary" icon={<PlusCircleOutlined />} onClick={handleAdd}>
                    Thêm câu hỏi
                </Button>
            </div>

            <Table
                tableLayout='fixed'
                columns={columns}
                bordered
                dataSource={questions}
                rowKey="id"
                scroll={{ y: 600 }}
                pagination={{
                    size: 'small',
                    current: currentPage,
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: [5, 10, 20],
                    showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
                }}
                loading={loading}
                onChange={handleTableChange}
            />

            <Modal
                title={editingQuestion?.id ? "Chỉnh sửa câu hỏi" : "Thêm câu hỏi mới"}
                open={showEditModal}
                onCancel={() => setShowEditModal(false)}
                onOk={handleSave}
                width={1500}
                centered
                bodyStyle={{ overflow: 'hidden' }}
                modalRender={(node) => (
                    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {node}
                    </div>
                )}
                styles={{
                    mask: {
                        backgroundColor: 'rgba(0, 0, 0, 0.45)'
                    },
                    body: {
                        maxHeight: 'calc(100vh - 200px)',
                        overflow: 'auto'
                    },
                    header: {
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'white',
                        padding: '16px 24px',
                        borderBottom: '1px solid #f0f0f0'
                    },
                    footer: {
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                        backgroundColor: 'white',
                        padding: '10px 16px',
                        borderTop: '1px solid #f0f0f0'
                    }
                }}
            >
                <Form layout="vertical" className="space-x-4 flex flex-row w-full">
                    <div className="w-1/3">
                        <Form.Item label="Loại câu hỏi">
                            <Select
                                value={editingQuestion?.questionSkillId}
                                onChange={(value) =>
                                    setEditingQuestion((prev) => ({
                                        ...prev,
                                        questionSkillId: value,
                                        QuestionSkill: questionSkills.find(qs => qs.id === value)
                                    }))
                                }
                            >
                                {questionSkills?.map((questionSkill) => (
                                    <Select.Option
                                        key={questionSkill?.id}
                                        value={questionSkill?.id}
                                    >
                                        {questionSkill?.TypeQuestion?.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Câu hỏi số">
                            <Input
                                type="number"
                                value={editingQuestion?.no}
                                onChange={(e) =>
                                    setEditingQuestion((prev) => ({ ...prev, no: Number(e.target.value) }))
                                }
                            />
                        </Form.Item>
                        <Form.Item label="Tiêu đề">
                            <Input.TextArea
                                rows={4}
                                value={editingQuestion?.title}
                                onChange={(e) =>
                                    setEditingQuestion((prev) => ({ ...prev, title: e.target.value }))
                                }
                            />
                        </Form.Item>
                        <Form.Item label="Nội dung">
                            <Input.TextArea
                                rows={15}
                                value={editingQuestion?.content}
                                onChange={(e) =>
                                    setEditingQuestion((prev) => ({ ...prev, content: e.target.value }))
                                }
                            />
                        </Form.Item>
                        <Form.Item label="Link âm thanh">
                            <Input.TextArea
                                rows={4}
                                value={editingQuestion?.linkAudio}
                                onChange={(e) => {
                                    setEditingQuestion((prev) => ({ ...prev, linkAudio: e.target.value }))
                                }}
                            />
                        </Form.Item>
                        <Form.Item label="Phụ đề">
                            <Input.TextArea
                                rows={20}
                                value={editingQuestion?.transcript}
                                onChange={(e) =>
                                    setEditingQuestion((prev) => ({ ...prev, transcript: e.target.value }))
                                }
                            />
                        </Form.Item>
                    </div>
                    <Divider type="vertical" />
                    <div className="w-2/3">
                        <Form.Item label="Câu hỏi phụ">
                            {renderSubQuestions()}
                        </Form.Item>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default QuestionManagement;
